import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"

import { parseApiErrorMessage } from "../../../common/apiErrors"
import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import {
  MIN_INBOX_TEMPLATE_PROMPT_LEN,
  generateEmailInboxTemplate,
  generateSmsInboxTemplate,
} from "./inboxTemplateAi"

export default function InboxTemplateAIGenDialog({
  open,
  medium,
  onApply,
  onClose,
}) {
  const axios = useAuthorizedAxiosClient()
  const [prompt, setPrompt] = useState("")
  const [preview, setPreview] = useState(null)
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    setPrompt("")
    setPreview(null)
    setError("")
  }, [open, medium])

  async function runGenerate() {
    setIsLoading(true)
    setError("")
    try {
      const result =
        medium === "email"
          ? await generateEmailInboxTemplate(axios, prompt)
          : await generateSmsInboxTemplate(axios, prompt)
      setPreview(result)
    } catch ({ response }) {
      setPreview(null)
      setError(
        parseApiErrorMessage(
          response?.data,
          "Couldn't generate that template. Please try again."
        )
      )
    } finally {
      setIsLoading(false)
    }
  }

  function handleUseContent() {
    if (!preview?.aiReviewToken) return
    if (medium === "email") {
      onApply({
        draft: {
          name: preview.name || preview.subject || "",
          subject: preview.subject || "",
          html_body: preview.html_body || "",
        },
        aiReviewToken: preview.aiReviewToken,
        aiPrompt: prompt,
      })
    } else {
      onApply({
        draft: {
          name: preview.name || "",
          text: preview.text || "",
        },
        aiReviewToken: preview.aiReviewToken,
        aiPrompt: prompt,
      })
    }
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AutoAwesomeOutlinedIcon color="primary" />
        AI generate
      </DialogTitle>
      <DialogContent>
        <Column sx={{ gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Describe the reply you want. Generated content is ready to save
            without a separate review step.
          </Typography>
          {error ? (
            <Alert severity="error" onClose={() => setError("")}>
              {error}
            </Alert>
          ) : null}
          <TextField
            label="Instructions"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            multiline
            minRows={4}
            fullWidth
            helperText={`At least ${MIN_INBOX_TEMPLATE_PROMPT_LEN} characters`}
          />
          {preview ? (
            <Column
              sx={{
                gap: 1,
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
              }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                Generated draft
              </Typography>
              {medium === "email" ? (
                <>
                  <Typography variant="body2">
                    <strong>Template name:</strong>{" "}
                    {preview.name || preview.subject}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Subject:</strong> {preview.subject}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{ whiteSpace: "pre-wrap" }}
                  >
                    {preview.html_body}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2">
                    <strong>Name:</strong> {preview.name}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {preview.text}
                  </Typography>
                </>
              )}
            </Column>
          ) : null}
        </Column>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <LoadingButton
          variant="outlined"
          loading={isLoading}
          disabled={prompt.length < MIN_INBOX_TEMPLATE_PROMPT_LEN}
          onClick={runGenerate}
        >
          Generate
        </LoadingButton>
        <LoadingButton
          variant="contained"
          disabled={!preview?.aiReviewToken}
          onClick={handleUseContent}
        >
          Use this content
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
